/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Icon,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import cloneDeep from "lodash/cloneDeep";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import EditIcon from "@mui/icons-material/Edit";
import { tl, t } from "../../../components/translate";
import { getMedicineSearch } from "../../../api/prescrption";
import { medicineType, countableTypes } from "./medicineType";
import MedicationDialog, { DaysOfWeek } from "./MedicationDialog";
import { Medicine as MedicineInterface } from "../../../interfaces/AssessmentInterfaces";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";

// @ts-expect-error - css
import styles from "../Assessment.module.css";

interface MedicationPropsInterface {
  medication: MedicineInterface[];
  onChange: (med) => void;
  spID?: number;
  isOpd?: boolean;
  hideDefaultLabel?: boolean;
  customLabel?: string;
}

const StyledAutocomplete = styled(Autocomplete)({
  "& .MuiAutocomplete-option": {
    padding: 0
  }
});

const MedicationListView = (props) => {
  const { form, brand, strength, genericName, handleOpen } = props;
  return (
    <Box
      alignItems="center"
      display="flex"
      onClick={handleOpen}
      width="100%"
      pt="1rem"
      pb="1rem"
      pl="1rem"
    >
      <Box>{medicineType[form] && medicineType[form]()}</Box>
      <Typography component="span" variant="body2">
        <Box pl={1} display="inline">
          {brand}
        </Box>
        <Box pl={0.5} display="inline">
          {strength}
        </Box>
        <Box pl={1}>{genericName}</Box>
      </Typography>
    </Box>
  );
};

const convertMedicineItemToFormattedObject = (item: MedicineInterface) => ({
  id: item?.id ? item.id : "",
  duration: item.duration ? item.duration : 7,
  form: item.form ? item.form : "Tablet",
  meal: item.meal ? item.meal : "",
  brand: item.brand ? item.brand : item,
  remark: item.remark ? item.remark : "",
  strength: item.strength ? item.strength : "",
  frequency: item.frequency ? item.frequency : 0,
  genericName: item.genericName ? item.genericName : "",
  frequencyType: item.frequencyType ? item.frequencyType : "day",
  times: item.times ? item.times : "day",
  timesOfDay: item?.timesOfDay ? item?.timesOfDay : [],
  partsOfDay: item?.partsOfDay ? item?.partsOfDay : []
});

export const getMedicineMultiplier = (
  times: string,
  duration: number,
  frequencyType: string
): number => {
  if (times === frequencyType) {
    return duration;
  }
  if (Object.values(DaysOfWeek).includes(times as DaysOfWeek) && frequencyType === "week") {
    return duration;
  }
  if (times === "day" && frequencyType === "week") {
    return duration * 7;
  }
  if (times === "day" && frequencyType === "month") {
    return duration * 30;
  }
  if (times === "week" && frequencyType === "month") {
    return Math.round((duration * 30) / 7);
  }
  return 1;
};

export const showTimeUnit = (times: string): React.ReactNode | string => {
  if (times === "day") return tl("medication.day");
  if (times === "week") return tl("medication.week");
  if (times === "month") return tl("medication.month");
  return times || "";
};

export const showFrequencyUnit = (frequencyType: string, duration: number): string => {
  const isPlural = duration < 2 ? "" : "s";
  switch (frequencyType) {
    case "days":
      return `day${isPlural}.`;
    case "weeks":
      return `week${isPlural}.`;
    case "months":
      return `month${isPlural}.`;
    default:
      return frequencyType;
  }
};

export const showMeal = (meal: string): string => {
  if (meal === "before") return t("assessment.beforeMeal");
  if (meal === "after") return t("assessment.afterMeal");
  return "";
};
const Medication = ({
  medication,
  onChange,
  spID,
  isOpd = false,
  hideDefaultLabel = false,
  customLabel
}: MedicationPropsInterface): React.ReactElement => {
  const assessmentLabels = useAssessmentLabel();

  const [suggestions, setSuggestions] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [currentMedicineData, setCurrentMedicineData] = useState({});
  const [medicineData, setMedicineData] = useState<MedicineInterface[]>([]);

  const handleModalSave = (data: MedicineInterface) => {
    const formattedData = convertMedicineItemToFormattedObject(data);
    const updatedMedicineData = cloneDeep(medicineData);
    if (editingIndex >= 0) {
      // editing an existing item — replace in place
      updatedMedicineData.splice(editingIndex, 1, formattedData);
    } else {
      // adding a new item — remove the placeholder last entry and push
      updatedMedicineData.splice(updatedMedicineData.length - 1, 1, formattedData);
    }
    onChange(updatedMedicineData);
    setEditingIndex(-1);
    setModalIsOpen(false);
  };

  // use effects
  useEffect(() => {
    if (medication) {
      setMedicineData(medication);
    }
  }, [medication]);

  useEffect(() => {
    setCurrentMedicineData(medicineData[medicineData.length - 1]);
  }, [medicineData]);

  return (
    <>
      <>
        <Box className={styles.assessmentRow}>
          {!hideDefaultLabel && (
            <Box className={styles.assessmentLabel}>
              <Typography component="span" fontWeight="600">
                {isOpd ? assessmentLabels.medication : "Medication"}
              </Typography>
            </Box>
          )}

          <Box className={styles.assessmentField}>
            <StyledAutocomplete
              multiple
              freeSolo
              closeIcon={null}
              value={medicineData}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setModalIsOpen(true);
                  setSuggestions([]);
                }
              }}
              filterOptions={(options, params) => {
                if (params.inputValue !== "") {
                  options.push({
                    inputValue: params.inputValue,
                    brand: `Add "${params.inputValue}"`,
                    form: "",
                    strength: "",
                    genericName: ""
                  });
                }
                return options;
              }}
              filterSelectedOptions
              onChange={(
                event,
                newValue: string | Array<MedicineInterface & { inputValue: string }>
              ): void => {
                if (typeof newValue === "string") {
                  // timeout to avoid instant validation of the dialog's form.
                  setTimeout(() => {
                    setModalIsOpen(true);
                    setCurrentMedicineData(medicineData[medicineData.length - 1]);
                  });
                } else if (
                  newValue[newValue.length - 1] &&
                  newValue[newValue.length - 1]?.inputValue
                ) {
                  setModalIsOpen(true);

                  const tempArr = [];
                  for (let i = 0; i < newValue.length; i += 1) {
                    if (newValue[i].inputValue) {
                      const formattedV = convertMedicineItemToFormattedObject({
                        ...newValue[i],
                        brand: newValue[i].inputValue
                      });
                      tempArr.push(formattedV);
                    } else {
                      const formattedV = convertMedicineItemToFormattedObject(newValue[i]);
                      tempArr.push(formattedV);
                    }
                  }
                  onChange(tempArr);
                } else if (newValue.length >= medicineData.length) {
                  const tempArr = [];
                  for (let i = 0; i < newValue.length; i += 1) {
                    const formattedV = convertMedicineItemToFormattedObject(newValue[i]);
                    tempArr.push(formattedV);
                  }
                  onChange(tempArr);
                }
              }}
              onClose={() => {
                setSuggestions([]);
              }}
              options={suggestions}
              renderOption={(props, option: MedicineInterface) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...props} key={option.id}>
                  <MedicationListView
                    form={option?.form}
                    brand={option?.brand}
                    strength={option?.strength}
                    genericName={option?.genericName}
                    handleOpen={() => setModalIsOpen(true)}
                    times={option?.times}
                  />
                </li>
              )}
              renderTags={() => null} // doesnt render tags(chips)
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label={customLabel}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  data-testmation="medicationMainTextField"
                  placeholder={t("assessment.typeMedicationHere")}
                  onChange={(e) => {
                    if (e.target.value.length > 2) {
                      getMedicineSearch(e.target.value).then((res) => setSuggestions(res));
                    }
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </>

      <>
        {currentMedicineData && (
          <MedicationDialog
            handleCancel={() => {
              if (editingIndex < 0) {
                // was adding a new item — remove the pending placeholder
                const updatedMedicineData = medicineData.slice(0, -1);
                onChange(updatedMedicineData);
              }
              setEditingIndex(-1);
              setModalIsOpen(false);
            }}
            handleModalSave={handleModalSave}
            modalIsOpen={modalIsOpen}
            data={currentMedicineData}
            spID={spID}
          />
        )}
      </>

      <>
        <Box className={styles.assessmentRow}>
          <Box className={styles.assessmentLabel} />
          <Box
            className={customLabel ? "" : styles.assessmentField}
            width={customLabel ? "100%" : undefined}
          >
            {medicineData && medicineData.length > 0 && (
              <TableContainer
                sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflowX: "auto" }}
              >
                <Table size="small" sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 50 }}>S.N.</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Medicine</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Frequency / Instruction</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration & Form</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80, textAlign: "center" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicineData.map((item, index) => (
                      <TableRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item?.id}-${index}`}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell sx={{ verticalAlign: "top" }}>{index + 1}.</TableCell>

                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Icon style={{ color: "black", fontSize: "1.2rem" }}>
                              {medicineType[item.form]
                                ? medicineType[item.form]()
                                : medicineType.Tablet()}
                            </Icon>
                            <Box>
                              <Box fontSize="0.95rem" fontWeight="600">
                                {`${item.brand} ${item.strength}`}
                              </Box>
                              <Box fontSize="0.85rem" color="text.secondary">
                                {item.genericName}
                              </Box>
                              {item.remark && (
                                <Box fontSize="0.8rem" sx={{ mt: 0.5 }}>
                                  <b>{tl("assessment.medication.remark")}</b>: {item.remark}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Box>
                            {item.frequency === 0
                              ? ""
                              : `${item.frequency || ""} ${t("assessment.times")}`}
                            {item.frequency !== 0 && showTimeUnit(item.times)}
                            {item.meal && ` - ${showMeal(item.meal)}`}
                          </Box>
                          {item.partsOfDay && item.partsOfDay.length > 0 && (
                            <Box fontSize="0.8rem" color="text.secondary" sx={{ mt: 0.5 }}>
                              <b>{tl("assessment.medication.partsOfDay")}</b>:{" "}
                              {item.partsOfDay.map((tod) => t(tod) || tod).join(", ")}
                            </Box>
                          )}
                          {item.timesOfDay && item.timesOfDay.length > 0 && (
                            <Box fontSize="0.8rem" color="text.secondary" sx={{ mt: 0.5 }}>
                              <b>{tl("assessment.medication.timesOfDay")}</b>:{" "}
                              {item.timesOfDay.join(", ")}
                            </Box>
                          )}
                        </TableCell>

                        <TableCell sx={{ verticalAlign: "top" }}>
                          {item.frequency !== 0 && (
                            <>
                              {countableTypes.includes(item.form)
                                ? `${
                                    (item.frequency || 0) *
                                    getMedicineMultiplier(
                                      item.times,
                                      item.duration || 0,
                                      item.frequencyType
                                    )
                                  } `
                                : ""}
                              {`${item.form} (${item.duration || 0} ${showFrequencyUnit(
                                item.frequencyType,
                                item.duration
                              )})`}
                            </>
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            verticalAlign: "middle",
                            textAlign: "center",
                            whiteSpace: "nowrap"
                          }}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setEditingIndex(index);
                              setCurrentMedicineData(item);
                              setModalIsOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const updatedMedicineData = cloneDeep(medicineData);
                              updatedMedicineData.splice(index, 1);
                              onChange(updatedMedicineData);
                            }}
                          >
                            <HighlightOffIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </>
    </>
  );
};

export default Medication;
